import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useDiscussion } from "../../data/weatherQueries";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { NeedsLocation, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./nerd.css";

type DiscussionConfig = Record<string, never>;

/** The local NWS office's Area Forecast Discussion: what the forecasters are thinking, in their words. */
function Discussion({ size, fullscreen, openFullscreen }: WidgetProps<DiscussionConfig>) {
  const { settings } = useHousehold();
  useNow("minute");
  const q = useDiscussion();
  const state = dataStateOf(q, 6 * 60 * 60_000);
  if (!settings.location) return <NeedsLocation icon="note" title="Forecaster’s notes need a home location" detail="Set a general location to read your local forecast office’s notes." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="note" title="Forecaster’s notes unavailable" detail={q.error?.message} />;
  const sections = fullscreen ? data.sections : data.sections.slice(0, 2);

  return (
    <div className="nerd nerd-afd">
      <WidgetHeader title="Forecaster’s notes" meta={`NWS ${data.office}${data.issuedAt ? ` · ${relativeAge(data.issuedAt)}` : ""}`} onTitle={fullscreen ? undefined : openFullscreen} />
      {sections.length === 0 ? (
        <p className="muted">The latest discussion has no readable sections.</p>
      ) : (
        <div className="afd-body">
          {sections.map((s) => (
            <section key={s.title}>
              <h4 className="sky-section-title">{s.title}</h4>
              {s.text.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </section>
          ))}
          {!fullscreen && data.sections.length > 2 && (
            <button className="link-btn" onClick={openFullscreen}>
              Read all {data.sections.length} sections
            </button>
          )}
        </div>
      )}
      {size.variant !== "compact" && <SkyFoot source="NWS Area Forecast Discussion" stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

export const discussionWidget: WidgetDefinition<DiscussionConfig> = {
  type: "forecast-discussion",
  version: 1,
  name: "Forecaster’s notes",
  description: "Your local National Weather Service office’s forecast discussion: key messages and reasoning behind the forecast.",
  icon: "note",
  category: "weather",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 3, h: 3 },
  stackHeight: 380,
  defaults: {},
  settings: [],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Discussion,
};
